import { saveAs } from 'file-saver';

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const DARK_BLUE = 'FF123247';
const WHITE = 'FFFFFFFF';

const TIPO_ARGB = {
  APERTURA:   { bg: 'FFE3F2FD', color: 'FF1565C0', label: 'Apertura' },
  EGRESO:     { bg: 'FFFCE4EC', color: 'FFB71C1C', label: 'Egreso' },
  REPOSICION: { bg: 'FFE8F5E9', color: 'FF1B5E20', label: 'Reposición' },
  AJUSTE:     { bg: 'FFFFF3E0', color: 'FFE65100', label: 'Ajuste' },
  CIERRE:     { bg: 'FFF3E5F5', color: 'FF4A148C', label: 'Cierre' },
};

function fmtDate(d) {
  if (!d) return '';
  const s = String(d).split('T')[0];
  const [y, m, dd] = s.split('-');
  return `${dd}/${m}/${y}`;
}

function setThinBorder(cell, color = 'FFD0D7DE') {
  cell.border = {
    top:    { style: 'thin', color: { argb: color } },
    left:   { style: 'thin', color: { argb: color } },
    bottom: { style: 'thin', color: { argb: color } },
    right:  { style: 'thin', color: { argb: color } },
  };
}

function styleHeaderRow(row, numCols) {
  row.height = 24;
  for (let i = 1; i <= numCols; i++) {
    const cell = row.getCell(i);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK_BLUE } };
    cell.font = { bold: true, color: { argb: WHITE }, size: 10 };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    setThinBorder(cell, DARK_BLUE);
  }
}

function addKpi(ws, labelRange, valueRange, label, value, fill) {
  ws.mergeCells(labelRange);
  const lc = ws.getCell(labelRange.split(':')[0]);
  lc.value = label;
  lc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  lc.font = { bold: true, color: { argb: WHITE }, size: 10 };
  lc.alignment = { vertical: 'middle', horizontal: 'center' };
  setThinBorder(lc, fill);

  ws.mergeCells(valueRange);
  const vc = ws.getCell(valueRange.split(':')[0]);
  vc.value = value;
  vc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7FAFC' } };
  vc.font = { bold: true, size: 15, color: { argb: DARK_BLUE } };
  vc.alignment = { vertical: 'middle', horizontal: 'center' };
  setThinBorder(vc, 'FFD7E3EC');
}

function createResumenSheet(workbook, { cajas, filtroText, usuario }) {
  const cols = [
    { header: 'Caja',           width: 24 },
    { header: 'Responsable',    width: 22 },
    { header: 'Moneda',         width: 10 },
    { header: 'Fondo Asignado', width: 16 },
    { header: 'Saldo Actual',   width: 16 },
    { header: 'Estado',         width: 12 },
    { header: 'Total Egresos',  width: 16 },
    { header: 'Cant. Egresos',  width: 14 },
    { header: 'Total Repos.',   width: 16 },
    { header: 'Total Ajustes',  width: 16 },
    { header: 'Apertura',       width: 14 },
    { header: 'Cierre',         width: 14 },
  ];
  const numCols = cols.length;
  const lastCol = String.fromCharCode(64 + numCols); // 'L'

  const ws = workbook.addWorksheet('Resumen', {
    views: [{ state: 'frozen', ySplit: 9 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  ws.properties.defaultRowHeight = 20;
  ws.columns = cols.map(c => ({ width: c.width }));

  // Title
  ws.mergeCells(`A1:${lastCol}1`);
  Object.assign(ws.getCell('A1'), {
    value: 'REPORTE - CAJA CHICA',
    font: { bold: true, size: 16, color: { argb: WHITE } },
    alignment: { horizontal: 'center', vertical: 'middle' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK_BLUE } },
  });
  ws.getRow(1).height = 26;

  // Timestamp
  ws.mergeCells(`A2:${lastCol}2`);
  Object.assign(ws.getCell('A2'), {
    value: `Generado el ${new Date().toLocaleString('es-PE')}${usuario ? ` | Usuario: ${usuario}` : ''}`,
    font: { italic: true, size: 10, color: { argb: 'FF475467' } },
    alignment: { horizontal: 'center' },
  });
  ws.getRow(2).height = 18;

  // Filter
  ws.mergeCells(`A3:${lastCol}3`);
  Object.assign(ws.getCell('A3'), {
    value: `Filtro: ${filtroText}`,
    font: { size: 10, color: { argb: 'FF344054' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7FA' } },
    alignment: { horizontal: 'left' },
  });

  // KPIs rows 5-7
  const totalCajas = cajas.length;
  const totalEgresos = cajas.reduce((s, c) => s + Number(c.total_egresos || 0), 0);
  const totalRepos   = cajas.reduce((s, c) => s + Number(c.total_reposiciones || 0), 0);
  addKpi(ws, 'A5:B5', 'A6:B7', 'Cajas en reporte', totalCajas,                         'FF175CD3');
  addKpi(ws, 'D5:E5', 'D6:E7', 'Total Egresos',    `S/ ${totalEgresos.toFixed(2)}`,    'FFB71C1C');
  addKpi(ws, 'G5:H5', 'G6:H7', 'Total Repos.',     `S/ ${totalRepos.toFixed(2)}`,      'FF027A48');

  // Header row 9
  const HROW = 9;
  const headerRow = ws.getRow(HROW);
  headerRow.values = cols.map(c => c.header);
  styleHeaderRow(headerRow, numCols);

  // Data rows
  const MONEY_COLS = new Set([4, 5, 7, 9, 10]);
  cajas.forEach((row, idx) => {
    const excelRow = ws.getRow(HROW + 1 + idx);
    excelRow.values = [
      row.nombre || '',
      row.responsable_nombre || '',
      row.moneda || '',
      Number(row.fondo_asignado    || 0),
      Number(row.saldo_actual      || 0),
      row.estado || '',
      Number(row.total_egresos     || 0),
      Number(row.cant_egresos      || 0),
      Number(row.total_reposiciones|| 0),
      Number(row.total_ajustes     || 0),
      fmtDate(row.fecha_apertura),
      fmtDate(row.fecha_cierre),
    ];
    excelRow.height = 20;
    excelRow.eachCell((cell, colNum) => {
      cell.alignment = { vertical: 'middle', horizontal: MONEY_COLS.has(colNum) ? 'right' : 'left' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: idx % 2 === 0 ? 'FFF8FAFC' : WHITE } };
      setThinBorder(cell);
      if (MONEY_COLS.has(colNum) && colNum !== 8) cell.numFmt = '#,##0.00';
    });
    // Estado color
    const ec = excelRow.getCell(6);
    if (row.estado === 'ACTIVA') {
      ec.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
      ec.font = { bold: true, color: { argb: 'FF1B5E20' } };
    } else if (row.estado === 'CERRADA') {
      ec.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
      ec.font = { bold: true, color: { argb: 'FF344054' } };
    }
  });

  ws.autoFilter = { from: { row: HROW, column: 1 }, to: { row: HROW, column: numCols } };

  const totRow = ws.getRow(HROW + cajas.length + 1);
  totRow.getCell(1).value = `Total: ${totalCajas} caja(s)`;
  totRow.eachCell(cell => {
    cell.font = { bold: true, color: { argb: DARK_BLUE } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAF2F8' } };
    setThinBorder(cell, 'FFB9C8D6');
  });
}

function createMovimientosSheet(workbook, { movimientos, filtroText, usuario }) {
  const cols = [
    { header: 'Fecha',           width: 14 },
    { header: 'Caja',            width: 24 },
    { header: 'Tipo',            width: 14 },
    { header: 'Concepto',        width: 28 },
    { header: 'Categoría',       width: 20 },
    { header: 'Nº Comprobante',  width: 16 },
    { header: 'Proveedor',       width: 22 },
    { header: 'Monto',           width: 14 },
    { header: 'Saldo Anterior',  width: 14 },
    { header: 'Saldo Posterior', width: 15 },
    { header: 'Usuario',         width: 14 },
    { header: 'Observaciones',   width: 24 },
  ];
  const numCols = cols.length;
  const lastCol = String.fromCharCode(64 + numCols); // 'L'

  const ws = workbook.addWorksheet('Movimientos', {
    views: [{ state: 'frozen', ySplit: 5 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  ws.properties.defaultRowHeight = 20;
  ws.columns = cols.map(c => ({ width: c.width }));

  // Title
  ws.mergeCells(`A1:${lastCol}1`);
  Object.assign(ws.getCell('A1'), {
    value: 'MOVIMIENTOS - CAJA CHICA',
    font: { bold: true, size: 16, color: { argb: WHITE } },
    alignment: { horizontal: 'center', vertical: 'middle' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: DARK_BLUE } },
  });
  ws.getRow(1).height = 26;

  // Timestamp
  ws.mergeCells(`A2:${lastCol}2`);
  Object.assign(ws.getCell('A2'), {
    value: `Generado el ${new Date().toLocaleString('es-PE')}${usuario ? ` | Usuario: ${usuario}` : ''}`,
    font: { italic: true, size: 10, color: { argb: 'FF475467' } },
    alignment: { horizontal: 'center' },
  });
  ws.getRow(2).height = 18;

  // Filter
  ws.mergeCells(`A3:${lastCol}3`);
  Object.assign(ws.getCell('A3'), {
    value: `Filtro: ${filtroText}`,
    font: { size: 10, color: { argb: 'FF344054' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7FA' } },
    alignment: { horizontal: 'left' },
  });

  // Header row 5
  const HROW = 5;
  const headerRow = ws.getRow(HROW);
  headerRow.values = cols.map(c => c.header);
  styleHeaderRow(headerRow, numCols);

  // Data rows
  movimientos.forEach((m, idx) => {
    const tc    = TIPO_ARGB[m.tipo] || TIPO_ARGB.AJUSTE;
    const signo = m.tipo === 'EGRESO' ? -1 : 1;

    const excelRow = ws.getRow(HROW + 1 + idx);
    excelRow.values = [
      fmtDate(m.fecha_movimiento),
      m.caja_nombre        || '',
      tc.label,
      m.concepto           || '',
      m.categoria          || '',
      m.nro_comprobante    || '',
      m.proveedor          || '',
      Number(m.monto       || 0) * signo,
      Number(m.saldo_anterior   || 0),
      Number(m.saldo_posterior  || 0),
      m.usuario            || '',
      m.observaciones      || '',
    ];
    excelRow.height = 20;

    excelRow.eachCell((cell, colNum) => {
      cell.alignment = { vertical: 'middle', horizontal: [8, 9, 10].includes(colNum) ? 'right' : 'left' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: idx % 2 === 0 ? 'FFF8FAFC' : WHITE } };
      setThinBorder(cell);
      if ([8, 9, 10].includes(colNum)) cell.numFmt = '#,##0.00';
    });

    // Tipo cell — color coded
    const tipoCell = excelRow.getCell(3);
    tipoCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: tc.bg } };
    tipoCell.font = { bold: true, color: { argb: tc.color } };
    tipoCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // Monto sign color
    const montoCell = excelRow.getCell(8);
    if (m.tipo === 'EGRESO')
      montoCell.font = { bold: true, color: { argb: 'FFB71C1C' } };
    else if (m.tipo === 'REPOSICION' || m.tipo === 'APERTURA')
      montoCell.font = { bold: true, color: { argb: 'FF1B5E20' } };
  });

  ws.autoFilter = { from: { row: HROW, column: 1 }, to: { row: HROW, column: numCols } };

  // Totals row
  const totRow = ws.getRow(HROW + movimientos.length + 1);
  totRow.getCell(1).value = `Total: ${movimientos.length} movimiento(s)`;
  const totalNeto = movimientos.reduce(
    (s, m) => s + (m.tipo === 'EGRESO' ? -Number(m.monto || 0) : Number(m.monto || 0)), 0
  );
  const montoTot = totRow.getCell(8);
  montoTot.value = totalNeto;
  montoTot.numFmt = '#,##0.00';
  totRow.eachCell(cell => {
    cell.font = { bold: true, color: { argb: DARK_BLUE } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAF2F8' } };
    setThinBorder(cell, 'FFB9C8D6');
  });
}

export async function exportCajaChicaWorkbook({ cajas, movimientos, filtros, usuario }) {
  const ExcelJSModule = await import('exceljs');
  const ExcelJS = ExcelJSModule.default || ExcelJSModule;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'InsightSoft';
  workbook.created = new Date();

  const parts = [];
  if (filtros.desde && filtros.hasta) parts.push(`${fmtDate(filtros.desde)} al ${fmtDate(filtros.hasta)}`);
  if (filtros.cajaId) parts.push(`Caja ID: ${filtros.cajaId}`);
  if (filtros.tipo)   parts.push(TIPO_ARGB[filtros.tipo]?.label || filtros.tipo);
  const filtroText = parts.length ? parts.join(' | ') : 'Sin filtros';

  createResumenSheet(workbook, { cajas, filtroText, usuario });
  createMovimientosSheet(workbook, { movimientos, filtroText, usuario });

  const buffer = await workbook.xlsx.writeBuffer();
  const timestamp = new Date().toISOString().slice(0, 10);
  saveAs(new Blob([buffer], { type: EXCEL_MIME }), `CajaChica-${timestamp}.xlsx`);
}
